import React from 'react';

import * as analytics from 'analytics/Analytics';
import {getObjectTypeForModelTypeConstant} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {ANALYTICS_MODULE, REGISTRY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {Bioentity} from 'folder-items/FileConstants';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {ComplexPolymerCreateStep} from 'modbio/complex-polymers/create/CreateComplexPolymerModal';
import {getInitialComplexPolymerTags} from 'modbio/complex-polymers/create/CreateComplexPolymerUtil';
import notifications from 'notifications';
import CreateAndRegisterBioentityInputs from 'registry/create-bioentity/CreateAndRegisterBioentityInputs';
import useTagSchemaQuery from 'schemas/useTagSchemaQuery';
import {isStudyableObjectType} from 'studies/studyable/StudyableUtils';
import {isDNAOligoSchema, isRNAOligoSchema} from 'tag-schema/util/TagSchemaUtil';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import Modal, {ModalProps} from 'ui-library/modals/Modal';
import * as net from 'util/Net';

import {
  CreateAndRegisterBioentityArgs,
  createAndRegisterWithDataModsOnMerge,
  isBioentityValidForCreation,
} from './CreateBioentityUtil';

interface BioentityArgsWithWeakTagSchema extends Omit<
  CreateAndRegisterBioentityArgs,
  'tagSchema' | 'complexPolymerTags'
> {
  tagSchemaId?: string | null;
}

// Other modules may add their own step types here if they wish to implement more customized behavior.
export type CreateAndRegisterStep = ComplexPolymerCreateStep;

export interface CreateAndRegisterBioentityModalProps {
  createAndRegisterBioentityArgs: BioentityArgsWithWeakTagSchema;
  onRequestClose: () => void;
}

export interface PresentationalCreateAndRegisterBioentityModalProps {
  createAndRegisterBioentityArgs: CreateAndRegisterBioentityArgs;
  onRequestClose: () => void;
}

interface State {
  createAndRegisterBioentityArgs: CreateAndRegisterBioentityArgs;
  errorMessage: string | null;
  createStep: CreateAndRegisterStep | null;
}

/**
 * A modal form to create and register an entity for a given schema.
 * Supports entering all fields for the given schema (except choosing a different schema) in a single pane.
 * Upon submission, this component will handle:
 * - The actual entity creation.
 * - Showing the user any validation errors (in a toast).
 */
export class CreateAndRegisterBioentityModal extends React.Component<
  PresentationalCreateAndRegisterBioentityModalProps,
  State
> {
  override state: State = {
    createAndRegisterBioentityArgs: this.props.createAndRegisterBioentityArgs,
    errorMessage: null,
    createStep: null,
  };

  _updateCreateStep = (createStep: CreateAndRegisterStep | null): void => {
    this.setState({createStep});
  };

  _updateCreateAndRegisterBioentityArgs = (values: Partial<CreateAndRegisterBioentityArgs>): void => {
    this.setState((oldState) => ({
      ...oldState,
      createAndRegisterBioentityArgs: {...oldState.createAndRegisterBioentityArgs, ...values},
    }));
  };

  _createAndRegisterBioentity = async (): Promise<void> => {
    try {
      const created: Bioentity | null = await createAndRegisterWithDataModsOnMerge(
        this.state.createAndRegisterBioentityArgs
      );
      if (created == null) {
        // data mods modal was closed, so cancel the submit operation
        return;
      }
      FileBrowserActions.openFile(created.id);
      analytics.deprecatedTrackEvent(REGISTRY_ANALYTICS_ACTIONS.CREATED_FILES_IN_REGISTRY_MODAL, {
        product: ANALYTICS_MODULE.REGISTRY,
        entity_id: created.id,
        entity_type: created.tagSchema!.folderItemType,
        schema_id: created.tagSchema!.id,
      });
      this.props.onRequestClose();
    } catch (e) {
      if (net.isServerRequestError(e) && notifications.getFriendlyError(e)) {
        this.setState({errorMessage: notifications.getFriendlyError(e)});
      } else {
        throw e;
      }
    }
  };

  override render(): JSX.Element {
    const optionalProps: Partial<ModalProps> = this.state.errorMessage
      ? {bannerProps: {bannerType: InlineBannerType.ERROR, text: this.state.errorMessage}}
      : {};
    const {tagSchema, complexPolymerSchemaConfig} = this.props.createAndRegisterBioentityArgs;
    const isMolecule = tagSchema!.folderItemType === ModelTypeConstants.MOLECULE;
    const objectType = getObjectTypeForModelTypeConstant(tagSchema!.folderItemType);
    const modalChildren = (
      <CreateAndRegisterBioentityInputs
        createAndRegisterBioentityArgs={this.state.createAndRegisterBioentityArgs}
        onChange={this._updateCreateAndRegisterBioentityArgs}
        labelStyle={LabelStyle.LABEL}
        showSchemaSelector={false}
        isOligoSchema={isDNAOligoSchema(tagSchema!) || isRNAOligoSchema(tagSchema!)}
        showStudiesField={objectType ? isStudyableObjectType(objectType) : false}
        createStep={this.state.createStep}
        setCreateStep={this._updateCreateStep}
      />
    );
    const modalProps: ModalProps = {
      title: `Create ${tagSchema ? tagSchema.name : ''}`,
      onRequestClose: this.props.onRequestClose,
      onRequestSubmit: this._createAndRegisterBioentity,
      scrollable: true,
      buttons: [
        {
          text: 'Register',
          submit: true,
          disabled: !isBioentityValidForCreation(this.state.createAndRegisterBioentityArgs),
        },
      ],
      children: modalChildren,
    };

    if (isMolecule) {
      return <ExpandableModal {...modalProps} {...optionalProps} startMaximized={true} />;
    } else if (
      complexPolymerSchemaConfig != null &&
      this.state.createStep === ComplexPolymerCreateStep.CONFIGURE_ATTACHMENTS
    ) {
      return (
        <Modal {...modalProps} {...optionalProps} fullWidth={true} fullHeight={true} bodyPadded={false} />
      );
    } else {
      return <Modal {...modalProps} {...optionalProps} />;
    }
  }
}

export default function QueryingCreateAndRegisterBioentityModal({
  createAndRegisterBioentityArgs,
  onRequestClose,
}: CreateAndRegisterBioentityModalProps): JSX.Element {
  const {tagSchemaId, ...rest} = createAndRegisterBioentityArgs;
  const {tagSchema, isLoading} = useTagSchemaQuery(tagSchemaId);

  if (isLoading) {
    return (
      <Modal title="Loading..." onRequestClose={onRequestClose}>
        <CenteredHorizontalThrobber />
      </Modal>
    );
  } else {
    const tagSchemaRest = tagSchema != null ? graphQLToREST('TagSchema', tagSchema) : null;
    const complexPolymerTags =
      tagSchemaRest != null
        ? getInitialComplexPolymerTags(tagSchemaRest.allFields, rest.complexPolymerSchemaConfig)
        : [];

    return (
      <CreateAndRegisterBioentityModal
        createAndRegisterBioentityArgs={{...rest, tagSchema: tagSchemaRest, complexPolymerTags}}
        onRequestClose={onRequestClose}
      />
    );
  }
}
